import { getFontData } from '../fonts/font-data.js';
import { CONFIG } from '../config.js';

// CSS partials
import { generateCSSVariables } from './partials/variables.css.js';
import { baseCSS } from './partials/base.css.js';
import { layoutOnsiteCSS } from './partials/layout-onsite.css.js';
import { layoutEmbedCSS } from './partials/layout-embed.css.js';
import { layoutStandaloneCSS } from './partials/layout-standalone.css.js';
import { layoutPreviewCSS } from './partials/layout-preview.css.js';
import { componentsCSS } from './partials/components.css.js';
import { inputsCSS } from './partials/inputs.css.js';
import { questionsCSS } from './partials/questions.css.js';
import { scrollbarCSS } from './partials/scrollbar.css.js';
import { responsiveCSS } from './partials/responsive.css.js';
import { layoutCssSelectorEmbedCSS } from './partials/layout-css-selector-embed.css.js';
/**
 * Generates the complete <style> block for a survey based on its style
 * configuration and survey type.
 *
 * Composes all CSS partials in the correct order:
 *   1. CSS custom properties (design tokens)
 *   2. Base styles (container, topbar, progress)
 *   3. Layout-specific styles (onsite | embed | standalone | preview)
 *   4. Component styles (avatar, buttons, branding, error)
 *   5. Input styles (radio, checkbox, text, textarea)
 *   6. Question-type styles (options, emoji, rating, ranking, matrix, dropdown)
 *   7. Scrollbar styles
 *   8. Responsive breakpoints
 *
 * @param {object} style - The survey style configuration object.
 * @param {string} surveyType - One of: 'onsite', 'embed', 'embedded', 'cssSelectorEmbed', 'previewCssSelectorEmbed', 'standalone', 'previewStandalone', 'preview', 'previewLink'.
 * @param {object} [options] - Layout options (currently only used by 'preview').
 * @returns {string} A complete <style>...</style> HTML string.
 */
export function getStyleData(style, surveyType, options = {}, can = {}) {
    // Determine which layout partial to use
    let layoutCSS = '';

    switch (surveyType) {
        case 'embed':
        case 'embedded':
            layoutCSS = layoutEmbedCSS(style);
            break;
        case 'cssSelectorEmbed':
        case 'previewCssSelectorEmbed':
            layoutCSS = layoutCssSelectorEmbedCSS(style);
            break;
        case 'standalone':
        case 'previewStandalone':
            layoutCSS = layoutStandaloneCSS(style);
            break;
        case 'preview':
            layoutCSS = layoutPreviewCSS(style, options);
            break;
        case 'previewLink':
            // previewLink uses onsite layout
            layoutCSS = layoutOnsiteCSS(style);
            break;
        case 'onsite':
        default:
            layoutCSS = layoutOnsiteCSS(style);
            break;
    }

    // Compose all partials into a single style block
    const stylesArray = [
        getFontData(CONFIG.cdnBaseUrl),
        generateCSSVariables(style),
        baseCSS,
        layoutCSS,
        componentsCSS,
        inputsCSS,
        questionsCSS,
        scrollbarCSS,
        responsiveCSS
    ];

    if ((style?.customCss?.enabled === true || style?.customCss?.enabled === '1') && style?.customCss?.cssCode && can?.customCss !== false) {
        let customCssContent = style.customCss.cssCode;
        stylesArray.push(customCssContent);
    }

    const css = stylesArray.join('\n');

    return `<style>${css}</style>`;
}

